import React, { useEffect, useState } from 'react';
import {
    AppBar, Toolbar, IconButton, TextField, Modal, Box, Badge,
    Avatar
} from '@mui/material';
import { FiSearch, FiBell } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { BASE_URL } from '../utils/endpoints';
import UserProfileDialog from './UserProfileDialog'; // Import the new component

const searchIconVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3, delay: 0.2 } },
    hover: { scale: 1.1, transition: { duration: 0.2 } },
    tap: { scale: 0.9 }
};

// Animation variants for icons (hover and tap)
const iconVariants = {
    hover: { scale: 1.1, transition: { duration: 0.2 } },
    tap: { scale: 0.9 }
};

// Animation variants for modals and dialogs
const modalVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -50, transition: { duration: 0.3 } }
};

// Animation variants for search modal divs
const searchDivVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, delay: i * 0.1 }
    }),
    hover: { scale: 1.05, transition: { duration: 0.2 } }
};

const Topbar = ({ sidebarOpen }) => {
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const token = localStorage?.getItem("authToken");

        const fetchProfile = async () => {
            try {
                const response = await fetch(`${BASE_URL}/profile`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch profile');
                }
                const data = await response.json();
                setUser(data.user); // Store the entire user object
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        };

        if (token) {
            fetchProfile();
        }
    }, []);

    const handleOpenSearchModal = () => {
        setIsSearchModalOpen(true);
    };

    const handleCloseSearchModal = () => {
        setIsSearchModalOpen(false);
    };

    const handleOpenProfileDialog = () => {
        setIsProfileDialogOpen(true);
    };

    const handleCloseProfileDialog = () => {
        setIsProfileDialogOpen(false);
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    width: sidebarOpen ? 'calc(100% - 300px)' : 'calc(100% - 90px)',
                    transition: 'width 0.3s, margin-left 0.3s',
                    backgroundColor: "#fff",
                    color: '#000',
                    boxShadow: 'none',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '12px 0',
                    // Apply backdrop filter only when search modal is NOT open, for cleaner look
                    backdropFilter: !isSearchModalOpen ? "blur(0)" : 'blur(4px)',
                }}
            >
                <Toolbar sx={{ justifyContent: 'flex-end' }}>
                    <motion.div
                        variants={searchIconVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        whileTap="tap"
                    >
                        <IconButton onClick={handleOpenSearchModal} sx={{ mr: 3 }}>
                            <FiSearch style={{ color: '#9ca3af' }} />
                        </IconButton>
                    </motion.div>
                    {/* <motion.div variants={iconVariants} whileHover="hover" whileTap="tap">
                        <IconButton sx={{ mr: 4 }}>
                            <Badge badgeContent={4} color="error">
                                <FiBell />
                            </Badge>
                        </IconButton>
                    </motion.div> */}
                    <motion.div variants={iconVariants} whileHover="hover" whileTap="tap">
                        {/* Added an outer div to make the Avatar clickable with motion animations */}
                        <div onClick={handleOpenProfileDialog} style={{ cursor: "pointer" }}>
                            <Avatar
                                sx={{ width: 32, height: 32 }}
                                src={user?.profile_pic || ''}
                                alt={`${user?.first_name || ''} ${user?.last_name || ''}`}
                            />
                        </div>
                    </motion.div>
                </Toolbar>
            </AppBar>

            <Modal
                open={isSearchModalOpen}
                onClose={handleCloseSearchModal}
                aria-labelledby="search-modal-title"
                sx={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mt: 10,
                    background: 'transparent', ml: 60,
                }}
                BackdropProps={{
                    style: { background: 'transparent' }, // Also make the backdrop transparent
                }}
            >
                <motion.div
                    variants={modalVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <Box
                        sx={{
                            borderRadius: 2,
                            boxShadow: 24,
                            p: 2,
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
                            {[
                                { href: "/gallery", label: "Gallery", bgcolor: '#6366f1', initial: 'G' },
                                { href: "/getproduct", label: "Product", bgcolor: '#10b981', initial: 'P' },
                                { href: "/brand", label: "Brand", bgcolor: '#f59e42', initial: 'B' },
                                { href: "/getcategories", label: "Category", bgcolor: '#f75d52', initial: 'C' },
                                { href: "/getslider", label: "Slider", bgcolor: '#8cf5ce', initial: 'S' },
                                { href: "/contact", label: "Contact", bgcolor: '#94b4ff', initial: 'C' }
                            ].map((item, index) => (
                                <motion.div
                                    key={item.href}
                                    custom={index}
                                    variants={searchDivVariants}
                                    initial="hidden"
                                    animate="visible"
                                    whileHover="hover"
                                >
                                    <Box
                                        component="a"
                                        href={item.href}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textDecoration: 'none',
                                            backdropFilter: 'blur(4px)',
                                            borderRadius: 2,
                                            p: 2,
                                            minWidth: 100,
                                            boxShadow: 1,
                                            transition: 'box-shadow 0.2s',
                                            '&:hover': { boxShadow: 10, backdropFilter: 'blur(4px)', },
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: item.bgcolor, mb: 1, width: 40, height: 40 }}>{item.initial}</Avatar>
                                        <Box sx={{ fontWeight: 600, color: '#24803F' }}>{item.label}</Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </Box>
                </motion.div>
            </Modal>

            <UserProfileDialog
                open={isProfileDialogOpen}
                onClose={handleCloseProfileDialog}
                user={user}
            />
        </>
    );
};

export default Topbar;